import type { ObjectId } from '@freightview/object-id'

import {
  vArray,
  vBoolean,
  vEmail,
  vNonEmptyString,
  vNumber,
  vObject,
  vOneOf,
  vOptional,
  type vOutput,
  vPicklist,
  vString,
} from '../validation/index.ts'
import { type Workflow, workflows } from './common.ts'
import {
  configurablePermissionTypes,
  type UserAccess,
  userAccessTypes,
} from './permissions.ts'

const accountUserProfileBase = vObject({
  email: vEmail,
  firstName: vNonEmptyString(),
  lastName: vNonEmptyString(),
  phoneNumber: vString(),
  title: vString(),
  tac: vOptional(vBoolean()),
})

const onboardingProfileSchema = vObject({
  ...accountUserProfileBase.entries,
  company: vString(),
})
const userSetupProfileSchema = vObject({
  ...accountUserProfileBase.entries,
  password: vString(),
})

export const accountUserProfileUpdateSchema = vOneOf([
  // order of these is important
  userSetupProfileSchema,
  onboardingProfileSchema,
  accountUserProfileBase,
])

export type OnboardingProfileDto = vOutput<typeof onboardingProfileSchema>
type UserSetupProfileDto = vOutput<typeof userSetupProfileSchema>
export type AccountUserProfileUpdateDto = vOutput<
  typeof accountUserProfileUpdateSchema
>

export const isOnboardingProfileDto = (
  v: AccountUserProfileUpdateDto,
): v is OnboardingProfileDto => !!(v as OnboardingProfileDto).company

export const isUserSetupProfileDto = (
  v: AccountUserProfileUpdateDto,
): v is UserSetupProfileDto => !!(v as UserSetupProfileDto).password

export interface AccountUserListingResponse {
  _id: ObjectId
  username: string
  firstName: string
  lastName: string
  phone?: string
  title?: string
  access: UserAccess
  owner: boolean
  markup?: UserMarkup
  removed?: boolean
  registered?: Date
  preferences?: {
    subscribedVendors?: string[]
    autoSubscribeNewVendors?: boolean
  }
  workflows?: Workflow[]
}

export const markupSettingsSchema = vObject({
  percent: vOptional(vNumber()),
  amount: vOptional(vNumber()),
  minimum: vOptional(vNumber()),
  maximum: vOptional(vNumber()),
})
export const workflowMarkupSchema = vObject({
  parcel: vOptional(markupSettingsSchema),
  ltl: vOptional(markupSettingsSchema),
  truckload: vOptional(markupSettingsSchema),
})
export type MarkupSettings = vOutput<typeof markupSettingsSchema>

export type WorkflowMarkup = vOutput<typeof workflowMarkupSchema>
export type UserMarkup = WorkflowMarkup

export const userAccessSchema = vObject({
  type: vPicklist(userAccessTypes),
  permissions: vOptional(vArray(vPicklist(configurablePermissionTypes))),
})

export const accountUserMutationSchema = vObject({
  markup: vOptional(workflowMarkupSchema),
  access: vOptional(userAccessSchema),
  workflows: vOptional(vArray(vPicklist(workflows))),
})
export type UserAccessMutationDto = vOutput<typeof userAccessSchema>
export type AccountUserMutationDto = vOutput<typeof accountUserMutationSchema>

export const accountUserSources = ['saml'] as const
export type AccountUserSource = (typeof accountUserSources)[number]

export const accountUserCreateSchema = vObject({
  email: vEmail,
  firstName: vOptional(vString()),
  lastName: vOptional(vString()),
  ...accountUserMutationSchema.entries,
})
export type AccountUserCreateDto = vOutput<typeof accountUserCreateSchema>
